class NarrativeCarousel extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
    this.isPlaying = false;
    this.isPaused = false;
    this.timer = null;
    this.progressTimer = null;
    this.slides = [];
    this.controls = [];
    this.videos = [];
    this.observer = null;
    this.isInViewport = false;
    
    this.init();
  }

  init() {
    this.setupElements();
    this.setupEventListeners();
    this.setupIntersectionObserver();
    this.checkReducedMotion();
    this.goToSlide(0, false); // Initialize first slide
  }

  setupElements() {
    this.slidesContainer = this.querySelector('.narrative-carousel__slides');
    this.slides = Array.from(this.querySelectorAll('.narrative-carousel__slide'));
    this.controls = Array.from(this.querySelectorAll('.narrative-carousel__control'));
    this.videos = Array.from(this.querySelectorAll('video'));
    
    // Set up video event listeners
    this.videos.forEach(video => this.setupVideoEvents(video));
  }

  setupEventListeners() {
    // Control navigation
    this.controls.forEach((control, index) => {
      control.addEventListener('click', (e) => this.handleControlClick(e, index));
    });

    // Keyboard navigation
    // this.addEventListener('keydown', (e) => this.handleKeydown(e));

    // Mouse events for pause on hover
    /*
    if (this.getAttribute('data-pause-on-hover') === 'true') {
      this.addEventListener('mouseenter', () => this.handleMouseEnter());
      this.addEventListener('mouseleave', () => this.handleMouseLeave());
    }
    */

    // Window events
    //window.addEventListener('blur', () => this.pause());
    //window.addEventListener('focus', () => this.handleWindowFocus());
    //document.addEventListener('visibilitychange', () => this.handleVisibilityChange());
  }

  setupVideoEvents(video) {
    video.addEventListener('loadedmetadata', () => {
      video.currentTime = 0;
    });
    
    video.addEventListener('canplay', () => {
      // Video is ready to play
    });
  }

  setupIntersectionObserver() {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        this.isInViewport = entry.isIntersecting;
        
        if (entry.isIntersecting) {
          this.handleEnterViewport();
        } else {
          this.handleExitViewport();
        }
      });
    }, options);

    this.observer.observe(this);
  }

  checkReducedMotion() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      this.setAttribute('data-autoplay', 'false');
    }
  }

  handleEnterViewport() {
    if (this.getAttribute('data-autoplay') === 'true' && !this.isPaused) {
      this.play();
    }
  }

  handleExitViewport() {
    this.pause();
  }

  handleControlClick(event, index) {
    event.preventDefault();
    
    if (index === this.currentIndex) {
      // Clicking current slide control toggles play/pause
      this.togglePlayPause();
    } else {
      // Navigate to different slide
      this.goToSlide(index, true);
      if (this.isInViewport && this.getAttribute('data-autoplay') === 'true') {
        this.play();
      }
    }
  }

  handleKeydown(event) {
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        this.previous();
        break;
      case 'ArrowRight':
        event.preventDefault();
        this.next();
        break;
      case ' ':
      case 'Enter':
        event.preventDefault();
        this.togglePlayPause();
        break;
      case 'Home':
        event.preventDefault();
        this.goToSlide(0, true);
        break;
      case 'End':
        event.preventDefault();
        this.goToSlide(this.slides.length - 1, true);
        break;
    }
  }

  handleMouseEnter() {
    if (this.isPlaying) {
      this.pause();
      this.wasPlayingBeforeHover = true;
    }
  }

  handleMouseLeave() {
    if (this.wasPlayingBeforeHover && this.isInViewport) {
      this.play();
      this.wasPlayingBeforeHover = false;
    }
  }

  handleWindowFocus() {
    if (this.isInViewport && this.getAttribute('data-autoplay') === 'true' && !this.isPaused) {
      this.play();
    }
  }

  handleVisibilityChange() {
    if (document.hidden) {
      this.pause();
    } else if (this.isInViewport && this.getAttribute('data-autoplay') === 'true' && !this.isPaused) {
      this.play();
    }
  }

  goToSlide(index, userInitiated = false) {
    if (index < 0 || index >= this.slides.length || index === this.currentIndex) {
      return;
    }

    const previousIndex = this.currentIndex;
    this.currentIndex = index;

    // Update slides visibility
    this.slides.forEach((slide, i) => {
      slide.classList.toggle('active', i === index);
      slide.setAttribute('aria-hidden', i !== index);
    });

    // Update controls
    this.controls.forEach((control, i) => {
      const isActive = i === index;
      control.classList.toggle('active', isActive);
      control.setAttribute('aria-selected', isActive);
      
      // Reset progress rings
      const progressBar = control.querySelector('.narrative-carousel__progress-bar');
      if (progressBar) {
        progressBar.style.strokeDashoffset = '100';
        progressBar.style.transition = 'none';
      }
    });

    // Handle video playback
    this.handleVideoPlayback(previousIndex, index);

    // Restart timer if playing
    if (this.isPlaying) {
      this.startTimer();
    }

    // Announce change to screen readers
    if (userInitiated) {
      this.announceSlideChange(index);
    }
  }

  handleVideoPlayback(previousIndex, currentIndex) {
    // Pause all videos
    this.videos.forEach(video => {
      video.pause();
      video.currentTime = 0;
    });

    // Play videos in current slide
    const currentSlide = this.slides[currentIndex];
    if (currentSlide) {
      const currentVideos = currentSlide.querySelectorAll('video');
      currentVideos.forEach(video => {
        video.currentTime = 0;
        video.play().catch(() => {
          // Handle autoplay restrictions gracefully
        });
      });
    }
  }

  play() {
    if (this.isPlaying) return;
    
    this.isPlaying = true;
    this.isPaused = false;
    this.updatePlayPauseButtons();
    this.startTimer();
  }

  pause() {
    this.isPlaying = false;
    this.clearTimers();
    this.updatePlayPauseButtons();
    
    // Pause current progress animation
    const currentControl = this.controls[this.currentIndex];
    if (currentControl) {
      const progressBar = currentControl.querySelector('.narrative-carousel__progress-bar');
      if (progressBar) {
        const computedStyle = getComputedStyle(progressBar);
        const currentDashoffset = computedStyle.strokeDashoffset;
        progressBar.style.strokeDashoffset = currentDashoffset;
        progressBar.style.transition = 'none';
      }
    }
  }

  togglePlayPause() {
    if (this.isPlaying) {
      this.isPaused = true;
      this.pause();
    } else {
      this.isPaused = false;
      if (this.isInViewport) {
        this.play();
      }
    }
  }

  next() {
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    this.goToSlide(nextIndex, true);
    this.updatePlayPauseButtons();
  }

  previous() {
    const prevIndex = this.currentIndex === 0 ? this.slides.length - 1 : this.currentIndex - 1;
    this.goToSlide(prevIndex, true);
    this.updatePlayPauseButtons();
  }

  startTimer() {
    this.clearTimers();

    const currentControl = this.controls[this.currentIndex];
    if (!currentControl) return;

    const duration = parseFloat(currentControl.getAttribute('data-duration')) * 1000;
    const progressBar = currentControl.querySelector('.narrative-carousel__progress-bar');

    if (progressBar) {
      // Configura a animação usando stroke-dashoffset para precisão
      // O valor inicial deve ser 100 (barra vazia), e ao final 0 (barra cheia)
      progressBar.style.transition = 'none';
      progressBar.style.strokeDashoffset = '100';
      // Força o repaint para garantir que a transição será aplicada
      void progressBar.offsetWidth;
      progressBar.style.transition = `stroke-dashoffset ${duration}ms linear`;
      progressBar.style.strokeDashoffset = '0';
    }

    // Timer para trocar o slide após o tempo correto
    this.timer = setTimeout(() => {
      this.next();
    }, duration);
  }

  clearTimers() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    if (this.progressTimer) {
      clearTimeout(this.progressTimer);
      this.progressTimer = null;
    }
  }

  updatePlayPauseButtons() {
    this.controls.forEach((control, index) => {
      const playPauseContainer = control.querySelector('.narrative-carousel__play-pause');
      const playIcon = control.querySelector('.icon-play');
      const pauseIcon = control.querySelector('.icon-pause');
      
      if (index === this.currentIndex) {
        // Show play/pause button for current slide
        if (playPauseContainer) {
          playPauseContainer.style.display = 'flex';
        }
        
        if (this.isPlaying) {
          if (playIcon) playIcon.style.display = 'none';
          if (pauseIcon) pauseIcon.style.display = 'block';
        } else {
          if (playIcon) playIcon.style.display = 'block';
          if (pauseIcon) pauseIcon.style.display = 'none';
        }
      } else {
        // Hide play/pause button for other slides
        /*if (playPauseContainer) {
          playPauseContainer.style.display = 'none';
        }*/
      }
    });
  }

  announceSlideChange(index) {
    const slide = this.slides[index];
    if (slide) {
      const title = slide.querySelector('.narrative-carousel__slide-title')?.textContent || `Slide ${index + 1}`;
      
      // Create announcement for screen readers
      const announcement = document.createElement('div');
      announcement.setAttribute('aria-live', 'polite');
      announcement.setAttribute('aria-atomic', 'true');
      announcement.className = 'sr-only';
      announcement.textContent = `Now showing: ${title}`;
      
      document.body.appendChild(announcement);
      
      // Remove announcement after screen reader has time to read it
      setTimeout(() => {
        document.body.removeChild(announcement);
      }, 1000);
    }
  }

  disconnectedCallback() {
    this.clearTimers();
    
    if (this.observer) {
      this.observer.disconnect();
    }
    
    window.removeEventListener('blur', () => this.pause());
    window.removeEventListener('focus', () => this.handleWindowFocus());
    document.removeEventListener('visibilitychange', () => this.handleVisibilityChange());
  }
}

// Register the custom element
if (!customElements.get('narrative-carousel')) {
  customElements.define('narrative-carousel', NarrativeCarousel);
}

// Utility function for screen reader only content
if (!document.querySelector('.sr-only-styles')) {
  const style = document.createElement('style');
  style.className = 'sr-only-styles';
  style.textContent = `
    .sr-only {
      position: absolute !important;
      width: 1px !important;
      height: 1px !important;
      padding: 0 !important;
      margin: -1px !important;
      overflow: hidden !important;
      clip: rect(0, 0, 0, 0) !important;
      white-space: nowrap !important;
      border: 0 !important;
    }
  `;
  document.head.appendChild(style);
}